import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-b1af0bea"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "assertion-list" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "assertion-list-header" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "text-end" }, "date"),
  /*#__PURE__*/_createElementVNode("div"),
  /*#__PURE__*/_createElementVNode("div"),
  /*#__PURE__*/_createElementVNode("div", null, "type"),
  /*#__PURE__*/_createElementVNode("div", null, "details")
], -1))
const _hoisted_3 = { class: "list-unstyled assertion-list-area" }
const _hoisted_4 = {
  key: 0,
  class: "assertion-diff"
}
const _hoisted_5 = {
  key: 0,
  class: "assertion-time-gap"
}
const _hoisted_6 = {
  key: 1,
  class: "time-ellipsis"
}
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, "⋮", -1))
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = {
  key: 2,
  class: "assertion-distance"
}
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "place-ellipsis" }, "⋮", -1))
const _hoisted_11 = { class: "distance-text" }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", null, " Are you sure you want to delete this detail? ", -1))
const _hoisted_14 = { class: "assertion-type" }

import { computed, ref } from 'vue'
import { Assertion, AssertionType } from '@/rd/ResearchDataModel'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { usePersonStore } from '@/rd/PersonStore'
import { useFamilyStore } from '@/rd/FamilyStore'
import { useAssertionStore } from '@/rd/AssertionStore'
import { usePlaceStore } from '@/rd/PlaceStore'
import { isDefined } from '@/util/TypeScriptUtil'
import { GeoUtil } from '@/util/GeoUtil'
import { useDataGroupStore } from '@/gp/DataGroupStore'
import { ItemPermissions } from '@/gp/GroupAdminModel'
import { CompositeId } from '@/rd/CompositeId'
import { LoadMode } from '@/util/AsyncData'
import AssertionSet from '@/explore/AssertionSet.vue'
import AssertionModal from '@/manage/AssertionModal.vue'
import Modal from '@/util/Modal.vue'
import { AsyncUtil } from '@/util/AsyncUtil'
import { hideModalAsync } from '@/util/AppUtil'
import { Trash2Icon } from '@zhuowenli/vue-feather-icons'


export default /*#__PURE__*/_defineComponent({
  __name: 'AssertionList',
  props: {
    personId: {},
    includeRelated: { type: Boolean },
    includeRestricted: { type: Boolean },
    ownerOnly: { type: Boolean },
    dimOtherOwners: { type: Boolean },
    showCounts: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;



const assertionModalRef = ref<InstanceType<typeof AssertionModal>>()
const confirmDeleteModalRef = ref<InstanceType<typeof Modal>>()

const dataGroupStore = useDataGroupStore()
const viewPersonStore = useViewPersonStore()
const personStore = usePersonStore()
const familyStore = useFamilyStore()
const assertionStore = useAssertionStore()
const placeStore = usePlaceStore()

const orderedTypes: string[] = [
  AssertionType.Living,
  AssertionType.Name,
  AssertionType.Gender,
  AssertionType.Child,
]

const groupIdRef = computed(() => CompositeId.getGroupId(props.personId))
const permissionsRef = computed(() =>  dataGroupStore.getAsyncPermissions(groupIdRef.value, LoadMode.EnsureLoaded)?.data ?? ItemPermissions.None)
const personGroupRef = computed(() => dataGroupStore.getAsyncGroup(groupIdRef.value, LoadMode.EnsureLoaded)?.data)
const allowEditRef = computed(() => (personGroupRef.value?.isProfile && !personGroupRef.value?.trackedGroupId && permissionsRef.value & ItemPermissions.Modify) != 0)
const personRef = computed(() => personStore.getAsyncPerson(props.personId)?.data) // loaded by parent
const viewPersonRef = computed(() => viewPersonStore.getAsyncPerson(props.personId)?.data) // loaded by parent
const birthDateRef = computed(() => (props.ownerOnly ? personRef.value?.displayProperties : viewPersonRef.value?.displayProperties)?.birthDate.date1)
const spouseViewFamiliesRef = computed(() => {
  if (!props.personId || !viewPersonRef.value || !props.includeRelated)
    return []

  return viewPersonStore.getLoadedSpouseFamilies(viewPersonRef.value?.id).spouseFamilies
})
const assertionIdsRef = computed(() => {
  if (!props.personId || !viewPersonRef.value)
    return []

  const keyLists = personStore.getAsyncAssertionKeyLists(viewPersonRef.value.matchIds)

  if (props.includeRelated) {
    const spouseFamilyIds = spouseViewFamiliesRef.value.flatMap(vf => vf.matchIds)
    keyLists.push(...familyStore.getAsyncAssertionKeyLists(spouseFamilyIds))
  }
  return keyLists.flatMap(list => list?.data?.keys ?? [])
})
const assertionSetsRef = computed(() => {
  if (!props.personId || !viewPersonRef.value)
    return []

  const hiddenAssertionTypes = ["Child", "Living"]
  const assertions = assertionStore.getAsyncAssertionList(assertionIdsRef.value)
    .map(a => a.data).filter(isDefined)
    .filter(a => props.includeRestricted || !a.restricted)
    .filter(a => !props.ownerOnly || CompositeId.hasGroupId(a.id, groupIdRef.value!))
    .filter(a => !hiddenAssertionTypes.includes(a.assertionType))

  if (props.ownerOnly) {
    const assertionSets = assertions.map(a => [a])
    assertionSets.sort((a, b) => Assertion.compareDate(a[0], b[0]) || compareType(a[0], b[0]))
    return assertionSets
  }

  const viewPersonIdMap = new Map<string, string>()
  for (const matchId of viewPersonRef.value.matchIds) {
    viewPersonIdMap.set(matchId, props.personId) // *:1
  }

  const viewFamilyIdMap = new Map(spouseViewFamiliesRef.value.flatMap(vf => vf.matchIds.map(id => [id, vf.id!])))

  const assertionSets = buildViewAssertionSets(assertions, viewPersonIdMap, viewFamilyIdMap)
  for (const set of assertionSets) {
    set.sort(Assertion.comparePrecedence)
  }
  assertionSets.sort((a, b) => Assertion.compareDate(a[0], b[0]) || compareType(a[0], b[0]))
  
  return assertionSets
})

const fillerRowCountRef = computed(() => Math.max(6 - assertionSetsRef.value.length, 0))

function buildViewAssertionSets(assertions: Assertion[], viewPersonIdMap: Map<string, string>, viewFamilyIdMap: Map<string, string>) {
  const assertionSetMap = new Map<string, Assertion[]>()
  for (const a of assertions) {
    const subjectViewId = a.subjectType == "Person" 
      ? viewPersonIdMap.get(a.subjectId!)
      : viewFamilyIdMap.get(a.subjectId!)

    const key = `${a.subjectType}:${subjectViewId}:${a.assertionType}:${a.precedence?.instanceId ?? 1}`
    const set = assertionSetMap.get(key)
    if (set) {
      set.push(a)
    } else {
      assertionSetMap.set(key, [a])
    }
  }
  return Array.from(assertionSetMap.values())
}

const assertionTimeGapMap = computed(() => {
  const map = new Map<string, number>()
  let current = assertionSetsRef.value.at(0)
  for (let i = 1; i < assertionSetsRef.value.length; i++) {
    const prev = current!
    current = assertionSetsRef.value[i]

    if (!prev[0].sortDate.isEmpty && !current[0].sortDate.isEmpty) {
      // don't include gap between birth and first assertion since the gap is equal to the age, which is already shown
      if (birthDateRef.value && prev[0].sortDate.date1.diff(birthDateRef.value).years > 1) {
        const gap = current[0].sortDate.date1.diff(prev[0].sortDate.date1)
        map.set(current[0].id!, gap.years)
      }
    }
  }
  return map
})

const assertionDistanceMap = computed(() => {
  const map = new Map<string, number>()
  for (let i = 1; i < assertionSetsRef.value.length; i++) {
    const prev = assertionSetsRef.value[i - 1]
    const current = assertionSetsRef.value[i]

    if (prev[0].placeId && current[0].placeId) {
      const prevPlace = placeStore.trackAsyncPlace(prev[0].placeId)!.data
      const currentPlace = placeStore.trackAsyncPlace(current[0].placeId)!.data

      if (prevPlace && currentPlace) {
        const distance = prevPlace.distanceTo(currentPlace)
        if (distance) {
          map.set(current[0].id!, distance)
        }
      }
    }
  }
  return map
})

function showTimeGap(id: string) {
  const gap = assertionTimeGapMap.value.get(id)
  return gap && gap > 10 // years
}

function getTimeGapText(id: string) {
  const gap = assertionTimeGapMap.value.get(id)
  return `${Math.floor(gap ?? 0)} years`
}

function showDistance(id: string) {
  const distance = assertionDistanceMap.value.get(id)
  return distance && distance > 150 // km, or ~100 miles
}

function getDistanceText(id: string) {
  const distance = assertionDistanceMap.value.get(id)
  return `${Math.floor(GeoUtil.kmToMi(distance ?? 0)).toLocaleString()} miles`
}

function compareType(a:Assertion, b:Assertion) {
  const aIndex = orderedTypes.indexOf(a.assertionType)
  const bIndex = orderedTypes.indexOf(b.assertionType)
  return (aIndex == -1 ? 999 : aIndex) - (bIndex == -1 ? 999 : bIndex) // ordered types sort first
}

function getDimmedIds(set: Assertion[]) {
  if (!props.dimOtherOwners || !groupIdRef.value)
    return undefined

  return set.filter(a => !CompositeId.hasGroupId(a.id, groupIdRef.value!)).map(a => a.id!)
}

function onAssertionClick(id: string) {
  if (props.personId) {
    assertionModalRef.value?.open(id)
  }
}

function onAdd() {
  assertionModalRef.value?.open()
}

async function confirmDelete(assertionId?: string) {
  if (assertionId) {
    await hideModalAsync(assertionModalRef.value?.$el)
    const a = assertionStore.getAsyncAssertion(assertionId)?.data
    confirmDeleteModalRef.value!.open(a)
  }
}

async function onDelete(assertion?: Assertion) {
  if (assertion) {
    try {
      await AsyncUtil.atLeast(200, assertionStore.deleteAsync(assertion.id!))
      confirmDeleteModalRef.value?.close()
    }
    finally {
      confirmDeleteModalRef.value?.reset()
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("ul", _hoisted_3, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(assertionSetsRef.value, (set) => {
        return (_openBlock(), _createElementBlock("li", {
          key: set[0].id
        }, [
          (showTimeGap(set[0].id!) || showDistance(set[0].id!))
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                (showTimeGap(set[0].id!))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                      _createElementVNode("div", null, _toDisplayString(getTimeGapText(set[0].id!)), 1)
                    ]))
                  : _createCommentVNode("", true),
                (showTimeGap(set[0].id!) || showDistance(set[0].id!))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_6, _hoisted_8))
                  : _createCommentVNode("", true),
                (showDistance(set[0].id!))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                      _createElementVNode("div", null, [
                        _hoisted_10,
                        _createElementVNode("span", _hoisted_11, _toDisplayString(getDistanceText(set[0].id!)), 1)
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ]))
            : _createCommentVNode("", true),
          (_openBlock(), _createBlock(AssertionSet, {
            assertionIds: set.map(a => a.id!),
            key: set[0].id,
            "base-date": birthDateRef.value,
            "dimmed-ids": getDimmedIds(set),
            "show-count": _ctx.showCounts,
            onClick: onAssertionClick
          }, null, 8, ["assertionIds", "base-date", "dimmed-ids", "show-count"]))
        ]))
      }), 128)),
      (allowEditRef.value)
        ? (_openBlock(), _createElementBlock("li", _hoisted_12, [
            _createElementVNode("div", { class: "add-row" }, [
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-inline btn-link",
                onClick: onAdd
              }, " Add detail ")
            ])
          ]))
        : _createCommentVNode("", true),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(fillerRowCountRef.value, (i) => {
        return (_openBlock(), _createElementBlock("li", {
          key: i,
          class: "filler-row"
        }))
      }), 128))
    ]),
    _createVNode(AssertionModal, {
      ref_key: "assertionModalRef",
      ref: assertionModalRef,
      "context-person-id": _ctx.personId,
      onDelete: confirmDelete
    }, null, 8, ["context-person-id"]),
    _createVNode(Modal, {
      ref_key: "confirmDeleteModalRef",
      ref: confirmDeleteModalRef,
      class: "confirm-delete",
      "ok-style": "danger",
      onOk: onDelete
    }, {
      title: _withCtx(() => [
        _createTextVNode("Delete detail")
      ]),
      default: _withCtx(({ state }) => [
        _hoisted_13,
        _createElementVNode("div", _hoisted_14, _toDisplayString(state?.assertionType), 1)
      ]),
      okText: _withCtx(() => [
        _createVNode(_unref(Trash2Icon)),
        _createTextVNode(" Delete ")
      ]),
      _: 1
    }, 512)
  ]))
}
}

})